import { useCallback, useLayoutEffect, useRef, useState, type RefObject } from 'react'

// ── Learn-card prompt font ladder (ADR-012 §3/§4, ADR-027) ───────────────────
// Extracted verbatim from LearnPage.tsx so the hook can be unit-tested without
// mounting the whole page. Behaviour is unchanged by the move.

// Floor lowered 20 → 16 on the owner's instruction (2026-09-21, for on-device
// judgement; ADR not amended): C1 texts at 393x420 with the keyboard up still
// scrolled at 20. 18 added the same day, so text that just misses 20 does not
// drop to 16 and leave the space between empty. The upper rungs are unchanged,
// so short cards and keyboard-down states render exactly as before.
export const PROMPT_FONT_LADDER = [16, 18, 20, 24, 28, 32] as const  // ascending; floor 16, ceiling 32
export const PROMPT_FONT_LADDER_BASE_IDX = 4                 // base = 28
const PROMPT_FONT_STEPUP_SLACK_PX = 80                       // min spare px required to allow step-up
const PROMPT_FONT_STEPUP_MAX_CHARS = 24                      // only step up for genuinely short prompts

// Settle window for the ResizeObserver-driven ladder (ADR-027, amended
// 2026-08-31). The window closes when the measured element's geometry has been
// QUIET for LADDER_QUIET_MS, not on a fixed budget from card mount: a soft
// keyboard that raises slowly (cold IME on the first card of a session, or a
// re-raise after a bottom sheet closes) can push the post-keyboard geometry
// past any fixed deadline, and the old one froze the ladder against the
// pre-keyboard box. Every observed resize re-arms the quiet timer, so the
// window follows the keyboard instead of racing it.
// LADDER_SETTLE_MAX_MS is the absolute ceiling from the window's start, so a
// surface that never stops resizing still settles. A settled font holds until
// the box's height changes (see useLadderFontSize).
export const LADDER_QUIET_MS = 250
export const LADDER_SETTLE_MAX_MS = 1600

// Smallest content-box height at which a fit measurement means anything
// (ADR-027 amendment 2026-09-05).
//
// The ladder's only question is `scrollHeight > clientHeight + 1`. That test can
// tell one rung from another only if the box is tall enough to hold at least ONE
// line of prompt text at the ladder's floor font. Below that the test is true at
// every rung for every possible content — so walking the ladder measures
// nothing, and arriving at the floor is a fact about the box rather than a
// decision about the text.
//
// That is what "no usable height" means here: not a small number, but the height
// below which the measurement carries no information. The device capture
// happened to observe 0 px and 16 px (diagnostic §11), but pinning the rule to
// those values would be pinning it to one device's accident; stating it against
// the line box keeps it correct if the floor font or the observed values change.
//
// The multiplier is the SMALLEST line-height any prompt container applies — the
// definition zone's 1.35 (the FiB sentence uses 1.7). Taking the minimum is the
// conservative direction: a box that could fit a line in EITHER container must
// still be allowed to settle normally, so the gate must not engage above
// 16 × 1.35. Anything at or above this threshold behaves exactly as before,
// including a container that is genuinely too small for its content and
// correctly steps to the floor and scrolls.
const PROMPT_MIN_LINE_HEIGHT = 1.35
export const LADDER_MIN_USABLE_PX = PROMPT_FONT_LADDER[0] * PROMPT_MIN_LINE_HEIGHT  // 21.6

export type LadderState = { fontSize: number; shouldScroll: boolean }

/**
 * Measurement-based font ladder for the Learn-mode prompt zone (ADR-027).
 *
 * Renders at `ladder[baseIdx]` and steps down until content fits, enabling
 * internal scroll only at the floor. Step-up is conservative (short text +
 * significant slack, attempted once per card).
 *
 * Measurement is driven by a ResizeObserver on the element itself rather than
 * by viewportH re-renders. When the element's box changes (keyboard raise,
 * URL-bar appear/disappear, TWA WebView resize), the observer increments a
 * counter to force a re-render; the render-driven measure effect below then
 * reads fresh geometry. The walk settles once the element's box has been
 * quiet for LADDER_QUIET_MS (or immediately when scroll engages), with
 * LADDER_SETTLE_MAX_MS from the window's start as an absolute ceiling.
 *
 * resetKey identifies the text shown (Learn: card_id plus the reveal state); a
 * new window starts on each.
 *
 * A container that has not yet resolved to a usable height is not measured at
 * all, and neither the ladder nor the quiet window will settle against it
 * (ADR-027 amendment 2026-09-05, LADDER_MIN_USABLE_PX). On a multiple-choice
 * card the container reads 0 px ~15 ms after mount; that used to overflow at
 * every rung and freeze the ladder on the floor three renders later, before the
 * real geometry had arrived.
 *
 * Settlement is NOT final for the card (reverses ADR-027's one-way settle,
 * owner's instruction 2026-09-21, ADR-027 amendment pending). The settled box
 * height is recorded, the observer stays attached, and when the box later
 * resolves to a different height — keyboard raised or lowered after the window
 * closed, a sibling hidden or shown (Learn's supporting lines under the
 * reveal), a caption expanded — the walk re-opens from the base rung against
 * the new height. Observed on
 * device: a C1 sentence settled with the keyboard down kept its size after the
 * keyboard rose, and overflowed. Only the box's height re-opens it; a font
 * change inside the box does not change its height (it is flex-sized), so the
 * ladder's own steps cannot re-trigger it.
 *
 * `freeSpace`, when given, is the step before shrinking: called when the text
 * overflows at the base rung, it may give the box more height (Learn hides its
 * supporting lines under the reveal) and returns true if it did, in which case
 * the ladder re-measures before stepping down. It must return false once it has
 * nothing left to free.
 *
 * `freeAtFloor`, when given, is the step before scrolling: called when the text
 * still overflows at the floor, it may give the box more height (Learn shrinks,
 * then clamps, the translated caption beside the sentence) and returns true if
 * it did, in which case the walk starts again from the base — so the text ends
 * at the largest rung that fits the room it was given, not stuck at the floor.
 * It too must return false once it has nothing left to free.
 */
export function useLadderFontSize(
  ref: RefObject<HTMLElement | null>,
  ladder: readonly number[],
  baseIdx: number,
  resetKey: unknown,
  freeSpace?: () => boolean,
  freeAtFloor?: () => boolean,
): LadderState {
  const [idx, setIdx] = useState(baseIdx)
  const [shouldScroll, setShouldScroll] = useState(false)
  // The resetKey the committed idx/shouldScroll belong to. The reset effect
  // below queues state updates; until React has applied them, `idx` still
  // describes the PREVIOUS card (or, with the reveal prototype, the previous
  // text) while the DOM already holds the new one. Measuring in that window
  // reads new content at an old rung, and when that old rung was the floor the
  // step-down branch settles the ladder permanently — one synchronous commit
  // later the reset raises the font back to the base, and it is frozen there
  // over content that overflows. The measure effect skips while this lags.
  const [stateKey, setStateKey] = useState(resetKey)
  const stepUpTriedRef = useRef(false)
  // true once the ladder has settled for this card; gates the measure effect
  const settledRef = useRef(false)
  // The box's clientHeight when it settled. A later, different height re-opens
  // the walk (see the doc comment).
  const settledHeightRef = useRef<number | null>(null)
  // The box's clientHeight at the last observed resize while a walk is open.
  // A walk only ever steps down, so a box that GROWS mid-walk (a soft keyboard
  // from the previous card still retracting as this one mounts) would leave the
  // text at a rung chosen for a box that no longer exists — see the observer.
  const walkHeightRef = useRef<number | null>(null)
  // Latest freeSpace, read from the measure effect without re-keying anything.
  const freeSpaceRef = useRef(freeSpace)
  const freeAtFloorRef = useRef(freeAtFloor)
  // Monotone counter used only to force re-renders when the element resizes
  const [, setMeasurePing] = useState(0)

  // Declared before the measure effect, so it always sees this render's value.
  useLayoutEffect(() => {
    freeSpaceRef.current = freeSpace
    freeAtFloorRef.current = freeAtFloor
  })

  // Per-card reset. Defined first so it runs before the RO effect.
  useLayoutEffect(() => {
    settledRef.current = false
    stepUpTriedRef.current = false
    setIdx(baseIdx)
    setShouldScroll(false)
    setStateKey(resetKey)
  }, [resetKey, baseIdx])

  // True once the element's box is tall enough for a fit test to mean anything.
  // clientHeight is the padding box, but the ladder only ever asks this while
  // shouldScroll is false — the H2 affordance's paddingBlock is added by the
  // floor branch, which is also the only branch that settles — so at every call
  // site below it is equal to the content box.
  const hasUsableHeight = useCallback(() => {
    const el = ref.current
    return !!el && el.clientHeight >= LADDER_MIN_USABLE_PX
  }, [ref])

  // Render-driven step-down loop. No deps → runs after every render while
  // !settled, reading the element's geometry at the currently committed font.
  useLayoutEffect(() => {
    const el = ref.current
    if (!el || settledRef.current) return
    // Committed state still belongs to the previous key — see stateKey above.
    if (stateKey !== resetKey) return
    // The container has no usable height yet — it is unresolved, not small.
    // Make no ladder decision of any kind here: do not step, do not engage the
    // floor, do not settle. Hold the current rung and wait for the observer to
    // deliver real geometry, which is then evaluated from the base font in the
    // ordinary way. Without this gate a 0 px box overflows at all four rungs and
    // the floor branch below makes that non-measurement permanent within one
    // synchronous commit chain (diagnostic §2.1, §4.2, §11).
    if (!hasUsableHeight()) return
    const overflowing = el.scrollHeight > el.clientHeight + 1
    if (overflowing) {
      // Free space before shrinking: if the caller can give the box more
      // height, measure again at this same rung on the next render (pinged
      // here, so the hook does not depend on the caller re-rendering).
      if (idx === baseIdx && freeSpaceRef.current?.()) {
        setMeasurePing(p => p + 1)
        return
      }
      if (idx > 0) {
        if (idx > baseIdx) stepUpTriedRef.current = true
        setIdx(idx - 1)
      } else if (!shouldScroll && freeAtFloorRef.current?.()) {
        // Room was freed below the text: walk again from the base against it.
        stepUpTriedRef.current = true
        setIdx(baseIdx)
        setMeasurePing(p => p + 1)
      } else if (!shouldScroll) {
        setShouldScroll(true)
        settledRef.current = true   // floor + scroll engaged → settled immediately
        settledHeightRef.current = el.clientHeight
      }
    } else if (idx === baseIdx && !stepUpTriedRef.current && !shouldScroll && idx + 1 < ladder.length) {
      const text = (el.textContent ?? '').trim()
      const slack = el.clientHeight - el.scrollHeight
      if (text.length > 0 && text.length <= PROMPT_FONT_STEPUP_MAX_CHARS && slack >= PROMPT_FONT_STEPUP_SLACK_PX) {
        stepUpTriedRef.current = true
        setIdx(idx + 1)
      }
    }
    // content fits at current size → timer will settle after the window
  })

  // ResizeObserver on the element's own box. Fires when the box actually
  // changes (keyboard raise, URL bar, WebView resize, a sibling hidden or
  // shown) even when visualViewport.resize does not. Increments measurePing to
  // trigger a re-render so the measure effect above re-runs at the new
  // geometry.
  //
  // While a walk is open, the window closes on geometric quiet: each observed
  // resize re-arms a LADDER_QUIET_MS timer, so the last measurement the ladder
  // sees is one taken at least that long after the element last moved; a slow
  // keyboard extends the window instead of being locked out by it.
  // LADDER_SETTLE_MAX_MS from the window's start caps it.
  //
  // After settling the observer stays attached. A resize that leaves the box's
  // height where it settled changes nothing; one that moves it re-opens the
  // walk from the base rung, with a fresh window.
  useLayoutEffect(() => {
    const el = ref.current
    if (!el) return
    let quietTimer: ReturnType<typeof setTimeout> | undefined
    let ceilingTimer: ReturnType<typeof setTimeout> | undefined
    // Function declarations: they call each other and are only ever invoked
    // from a timer, an observer callback, or after all are defined.
    function settle(reason: 'quiet' | 'ceiling') {
      // The quiet window asks "has the geometry stopped moving?". A container
      // that has not resolved to a usable height is not quiet — there is
      // nothing there yet to have moved — so closing the window on it would
      // make a measurement that means nothing permanent. Re-arm and keep
      // observing, the same way the window already extends itself for a
      // slow-raising keyboard. The ceiling below keeps this bounded.
      if (reason === 'quiet' && !hasUsableHeight()) { armQuiet(); return }
      if (reason === 'ceiling' && !hasUsableHeight()) {
        // Never resolved within the whole window. No font is readable in a box
        // this short, so settle on the most defensive presentation available —
        // the floor plus the H2 scroll affordance — which is exactly what the
        // ladder produces today for a container that genuinely stays too small.
        setIdx(0)
        setShouldScroll(true)
      }
      settledRef.current = true
      settledHeightRef.current = el!.clientHeight
      clearTimers()
    }
    function armQuiet() {
      if (settledRef.current) return
      if (quietTimer !== undefined) clearTimeout(quietTimer)
      quietTimer = setTimeout(() => settle('quiet'), LADDER_QUIET_MS)
    }
    function clearTimers() {
      if (quietTimer !== undefined) clearTimeout(quietTimer)
      if (ceilingTimer !== undefined) clearTimeout(ceilingTimer)
      quietTimer = undefined
      ceilingTimer = undefined
    }
    function openWindow() {
      clearTimers()
      ceilingTimer = setTimeout(() => settle('ceiling'), LADDER_SETTLE_MAX_MS)
      // Arm once up front so an element whose box never changes still settles.
      armQuiet()
    }

    const ro = new ResizeObserver(() => {
      if (settledRef.current) {
        const settledAt = settledHeightRef.current
        if (settledAt === null || Math.abs(el.clientHeight - settledAt) <= 1) return
        // The height the ladder settled against is gone: walk again from the
        // base, against the box as it is now.
        settledRef.current = false
        settledHeightRef.current = null
        stepUpTriedRef.current = false
        setIdx(baseIdx)
        setShouldScroll(false)
        openWindow()
      } else {
        // The box grew while the walk was open: any rung below the base was
        // chosen for a smaller box, and the walk never steps back up on its
        // own. Start again from the base against the box as it is now, as the
        // settled branch above does. Seen on device as a multiple-choice
        // sentence that grew when answered; reproduced by growing the viewport
        // ~200 ms after mount (a keyboard retracting as the card arrives): the
        // walk stepped to 20 px, then sat at 20 px in a box that held it at
        // 28 px, until answering moved the box a few pixels, re-opened the
        // settled walk, and it jumped to 28 px. A shrinking box (keyboard
        // rising) keeps walking down, unchanged.
        const prev = walkHeightRef.current
        if (prev !== null && el.clientHeight > prev + 1) {
          setIdx(baseIdx)
          setShouldScroll(false)
        }
        armQuiet()
      }
      walkHeightRef.current = el.clientHeight
      setMeasurePing(p => p + 1)
    })
    ro.observe(el)
    // A reset (new card, new text) always opens a fresh window; the per-card
    // reset effect above has already cleared settledRef.
    settledHeightRef.current = null
    walkHeightRef.current = el.clientHeight
    openWindow()

    return () => {
      clearTimers()
      ro.disconnect()
    }
    // hasUsableHeight is ref-stable; the window is keyed on the text shown.
  }, [resetKey, hasUsableHeight, baseIdx])

  return { fontSize: ladder[idx], shouldScroll }
}
